import { IGif, IImage } from '@giphy/js-types'
import { GifOverlayProps, SearchContext, VideoOverlay as VideoOverlaySDK } from '@giphy/react-components'
import copy from 'copy-text-to-clipboard'
import React, { DragEvent, useContext, useEffect, useState } from 'react'
import ReactDOMServer from 'react-dom/server'
import sanitize from 'sanitize-filename'
import styled, { css } from 'styled-components'
import { AppContext } from '../context'
import { linkButton } from '../img/images'
import AttributionOverlay from './attribution-overlay'
import GifDisplay from './gif-display'
import { Code, Download } from './overlay-icons'

const getVideoDownload = (gif: IGif) => {
    let result: IImage | undefined
    if (gif.video) {
        const { assets } = (gif.video as IGif['video'])!
        type AssetKeys = keyof typeof assets
        const rends: AssetKeys[] = ['1080p', '720p', '480p', '360p']
        rends.some((rend) => {
            result = assets[rend] as IImage
            return !!result
        })
    }
    return result
}

const TagOverlay = styled.div<{ isHovered: boolean; isDetail?: boolean }>`
    position: absolute;
    bottom: 0;
    color: white;
    justify-content: flex-start;
    align-items: flex-end;
    font-size: ${(props) => (props.isDetail ? 14 : 12)}px;
    white-space: nowrap;
    padding: 4px;
    overflow: hidden;
    width: 100%;
    text-overflow: ellipsis;
    background: linear-gradient(180deg, rgba(18, 18, 18, 0) 0%, rgba(18, 18, 18, 0.5) 100%);
    opacity: ${(props) => (props.isHovered ? 1 : 0)};
    transition: opacity 300ms;
    span {
        cursor: pointer;
    }
`

const VideoOverlay = styled(VideoOverlaySDK)`
    pointer-events: none;
    .${VideoOverlaySDK.imgClassName} {
        top: -3px;
        right: -2px;
        transform: scale(0.8);
    }
`

const ActionOverlay = styled.div<{ isHovered: boolean; notTransparent: boolean }>`
    opacity: ${(props) => (props.isHovered ? 1 : 0)};
    position: absolute;
    top: 0px;
    right: 0px;
    ${(props) =>
        props.notTransparent &&
        css`
            top: 3px;
            right: 3px;
        `}
    font-size: 10px;
    cursor: pointer;
    display: flex;
    ${(props) =>
        !props.notTransparent &&
        css`
            background: rgba(0, 0, 0, 0.3);
        `}
    div {
        padding: 2px;
    }
`

const EmojiVariationsOverlay = styled.div<{ isHovered: boolean }>`
    background: rgba(0, 0, 0, 0.7);
    cursor: default;
    display: flex;
    font-size: 12px;
    font-weight: bold;
    opacity: ${(props) => (props.isHovered ? 1 : 0)};
    padding: 2px;
    position: absolute;
    transition: opacity 150ms ease-in;
    right: 0;
    left: 0;
    top: 3px;
`

const TipOverlay = styled.div<{ isActive: boolean; notTransparent: boolean }>`
    border-radius: 5px;
    position: absolute;
    user-select: none;
    pointer-events: none;
    top: 0px;
    left: 0px;
    ${(props) =>
        props.notTransparent &&
        css`
            top: 3px;
            left: 3px;
        `}
    font-size: 10px;
    font-weight: bold;
    background: rgba(0, 0, 0, 0.9);
    opacity: ${(props) => (props.isActive ? 1 : 0)};
    transition: opacity 100ms ease-in-out;
    padding: 2px;
`

const Copy = styled.img`
    margin-top: -3px;
    margin-right: -3px;
    margin-left: -3px;
`

const Tag = ({ tag }: { tag: string }) => {
    const { setSearch, setActiveChannel } = useContext(SearchContext)
    return (
        <span
            onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                setActiveChannel(undefined)
                setSearch(tag)
            }}
        >
            #{tag}
            &nbsp;
        </span>
    )
}
const mapTags = (tags: string[] = []) => tags.map((tag) => (tag ? <Tag tag={tag} key={tag} /> : ''))
const downloadIconSize = 14
const largeIconSizeDownload = downloadIconSize - 2
const copyIconSize = 20
const largeIconSize = copyIconSize + 3
const Overlay = ({
    gif,
    isHovered,
    isDetail,
    isEmojiPopover,
    width,
}: GifOverlayProps & { isDetail?: boolean; isEmojiPopover?: boolean }) => {
    const { searchMode, videoMuted, setVideoMuted, config } = useContext(AppContext)
    const { activeChannel } = useContext(SearchContext)
    const [showTip, setShowTip] = useState<'embed' | 'link'>()

    useEffect(() => {
        if (!isHovered) {
            setShowTip(undefined)
        }
        if (showTip) {
            const timer = setTimeout(() => setShowTip(undefined), 1300)
            return () => clearTimeout(timer)
        }
    }, [showTip, isHovered])

    // eslint-disable-next-line camelcase
    const hideAttribution = !gif.user?.is_verified || activeChannel || isDetail
    const notTransparent = searchMode === 'gifs' || searchMode === 'videos' || !!isEmojiPopover
    const showVideoOverlay = searchMode === 'videos' && !isDetail
    const showFooter = searchMode === 'gifs' || searchMode === 'videos'
    const showEmojiVariationsOverlay = searchMode === 'emoji' && !!gif?.variation_count && !isDetail && !isEmojiPopover

    return (
        <div>
            <>
                {showVideoOverlay && width && (
                    <VideoOverlay
                        gif={gif}
                        width={width}
                        isHovered={isHovered}
                        muted={videoMuted}
                        hideMuteButton
                        onUserMuted={setVideoMuted}
                    />
                )}
                {config.isBrowserExtension && !showEmojiVariationsOverlay && (
                    <ActionOverlay isHovered={isHovered} notTransparent={notTransparent}>
                        {gif.type !== 'video' && (
                            <div
                                onClick={(e) => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    let url = gif.images.original.url
                                    let ext = `gif`
                                    if (gif.type === 'video') {
                                        const videoRendition = getVideoDownload(gif)
                                        if (videoRendition) {
                                            url = videoRendition.url
                                            ext = `mp4`
                                        } else {
                                            console.error(`Download:error getting rendition for ${gif.id}`)
                                        }
                                    }
                                    chrome.downloads?.download({
                                        url,
                                        filename: `${sanitize(gif.title)}.${ext}`,
                                    })
                                }}
                            >
                                <Download size={isDetail ? largeIconSizeDownload : undefined} />
                            </div>
                        )}
                        <div
                            onClick={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                copy(gif.bitly_url || gif.url)
                                setShowTip('link')
                            }}
                        >
                            <Copy
                                src={linkButton}
                                width={isDetail ? largeIconSize : copyIconSize}
                                height={isDetail ? largeIconSize : copyIconSize}
                            />
                        </div>
                        <div
                            onDragStart={(e: DragEvent<HTMLDivElement>) => {
                                const text = ReactDOMServer.renderToStaticMarkup(<GifDisplay gif={gif} />)
                                e.dataTransfer?.setData('text/html', text)
                                e.dataTransfer?.setData('text/plain', text)
                            }}
                            draggable
                            onClick={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                copy(ReactDOMServer.renderToStaticMarkup(<GifDisplay gif={gif} />))
                                setShowTip('embed')
                            }}
                        >
                            <Code size={isDetail ? 16 : undefined} />
                        </div>
                    </ActionOverlay>
                )}
                {showEmojiVariationsOverlay && (
                    <EmojiVariationsOverlay isHovered={isHovered}>More!</EmojiVariationsOverlay>
                )}
                <TipOverlay isActive={!!showTip && isHovered} notTransparent={notTransparent}>
                    {showTip === 'embed' ? 'Embed Code Copied!' : 'Link Copied!'}
                </TipOverlay>
                {!showFooter ? null : hideAttribution ? (
                    <TagOverlay isHovered={isHovered} isDetail={isDetail}>
                        {mapTags(gif.tags?.slice(0, 2))}
                    </TagOverlay>
                ) : (
                    <AttributionOverlay gif={gif} isHovered={isHovered} />
                )}
            </>
        </div>
    )
}

export default Overlay
